/**
 * Redesigned Cash Runway card (dashboards round 2, part 1d): shared between
 * the Overall dashboard (group scope) and the entity/Personal dashboard.
 *
 * Headline is exactly one of:
 *   - "X months (until MMM YYYY)" when burning cash
 *   - "Cash growing" (with the monthly rate alongside) when avg net cashflow
 *     is non-negative
 * Sublines are exactly two: current cash (the Bank Balance card figure,
 * passed in by the caller so it reconciles by construction) and avg net
 * cashflow over this year's complete months: the SAME window and figure as
 * the "Avg Net Cashflow (YTD, complete months)" stat card (Fan, 2026-10-25:
 * cash views tied). One sentence of method text. No other numbers.
 */
import { Calendar, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { computeCashRunwayCard } from '../../lib/cashRunwayCard';

export function CashRunwayCard({ cashRunway, currentCash, currency, formatCurrency }) {
  if (!cashRunway) return null;
  const model = computeCashRunwayCard(cashRunway, currentCash);

  const headline = model.cashPositive
    ? 'Cash growing'
    : model.runwayMonths != null
      ? `${model.runwayMonths} ${model.runwayMonths === 1 ? 'month' : 'months'} (until ${model.untilLabel})`
      : 'N/A';

  const color = model.cashPositive
    ? 'text-green-600 dark:text-green-400'
    : (model.runwayMonths ?? 0) < 3 ? 'text-red-600 dark:text-red-400'
    : (model.runwayMonths ?? 0) < 6 ? 'text-amber-600 dark:text-amber-400'
    : 'text-green-600 dark:text-green-400';

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            {model.cashPositive
              ? <TrendingUp className="h-4 w-4 text-primary" />
              : <Calendar className="h-4 w-4 text-primary" />}
          </div>
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">Cash Runway</p>
            <p className={`text-xl font-bold font-mono ${color}`}>
              {headline}
              {model.cashPositive && (
                <span className="text-sm font-normal ml-1.5">
                  ({formatCurrency(model.avgMonthlyNet, currency)}/mo)
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Current cash</span>
            <span className="font-mono">{formatCurrency(model.currentCash, currency)}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Avg net cashflow (YTD, complete months)</span>
            <span className="font-mono">{formatCurrency(model.avgMonthlyNet, currency)}/mo</span>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">{model.methodText}</p>
        {model.icExcludedCount > 0 && (
          <p className="text-[10px] text-muted-foreground mt-1">
            Excludes {model.icExcludedCount} intercompany transfer{model.icExcludedCount !== 1 ? 's' : ''} (internal cash movements, not burn)
          </p>
        )}
      </CardContent>
    </Card>
  );
}
